import { FitViewOptions, useReactFlow, useStore, useViewport } from "@xyflow/react";
import Icon from "app/components/shared/Icon";
import { useCallback } from "react";
import { ControlButton, ControlPanel } from "./ControlButton";
import { Fullscreen } from "./Fullscreen";

interface Props {
  fitViewOptions?: FitViewOptions;
  children?: React.ReactNode;
}

const FitToViewControl = ({ fitViewOptions = {} }) => {
  const { fitView } = useReactFlow();
  const handleFitView = useCallback(() => fitView({ duration: 450, ...fitViewOptions }), [fitView]);

  return (
    <ControlButton onClick={handleFitView} label="Fit to view">
      <Icon icon="location-crosshairs" className="h-4 w-5" />
    </ControlButton>
  );
};

const ZoomOutControl = () => {
  const { minZoomReached } = useStore((store) => ({
    minZoomReached: store.transform[2] <= store.minZoom,
  }));

  const { zoomOut } = useReactFlow();
  const handleZoomOut = useCallback(() => zoomOut(), [zoomOut]);

  return (
    <ControlButton onClick={handleZoomOut} label="Zoom out" disabled={minZoomReached}>
      <Icon icon="heroicons/outline/minus" className="h-4 w-5" />
    </ControlButton>
  );
};

const ZoomInControl = () => {
  const { maxZoomReached } = useStore((store) => ({
    maxZoomReached: store.transform[2] >= store.maxZoom,
  }));

  const { zoomIn } = useReactFlow();
  const handleZoomIn = useCallback(() => zoomIn(), [zoomIn]);

  return (
    <ControlButton onClick={handleZoomIn} label="Zoom in" disabled={maxZoomReached}>
      <Icon icon="heroicons/outline/plus" className="h-4 w-5" />
    </ControlButton>
  );
};

const ZoomLevel = () => {
  const { zoom } = useViewport();

  return (
    <span className="inline-block text-center min-w-[2.5rem] text-xs">
      {Math.ceil(zoom * 100)}%
    </span>
  );
};

export default function Controls({ children, ...props }: Props) {
  return (
    <div className="absolute bottom-3 right-3 flex gap-2 z-10">
      {children}

      <ControlPanel>
        <FitToViewControl {...props} />
      </ControlPanel>

      <ControlPanel>
        <ZoomOutControl />
        <ZoomLevel />
        <ZoomInControl />
      </ControlPanel>

      <ControlPanel>
        <Fullscreen />
      </ControlPanel>
    </div>
  );
}
